import logo from "@/assets/D_A_LOGO_trans.png";
import html2pdf from "html2pdf.js";
import httpClient from "@/services/httpClient";
import { server, previewFileUrl } from "@/services/constants";

class PDFCaseProgress {
    constructor() {
        this.data = null;
    }

    /**
     * ORIGINAL — unchanged. Opens the generated PDF in a new tab via a local
     * blob URL. Still used by whatever pages already call this.
     */
    async exportToPDF(data) {
        const pdfBlob = await this._generatePdfBlob(data);
        const pdfUrl = URL.createObjectURL(pdfBlob);
        window.open(pdfUrl, '_blank');
    }

    /**
     * NEW — separate function. Generates the exact same PDF, but uploads it
     * to the backend (which forwards it to file-preview-service) and opens
     * the resulting shareable URL instead of a local, temporary blob URL.
     *
     * @param {Object} data - same shape as exportToPDF: { caseInfo, latestUpdate, timelineData, caseId }
     * @returns {Promise<{url:string, folder:string, filename:string, moduleName:string|null, id:string|null, replacedOldFile:boolean}>}
     */
    async createToPDF(data, { open = true } = {}) {
        if (!data?.caseInfo || !data?.latestUpdate) {
            console.warn(
                "PDFCaseProgress.createToPDF: skipped — data is missing caseInfo/latestUpdate.",
                "Received:", data
            );
            return null;
        }

        const pdfBlob = await this._generatePdfBlob(data);

        const formData = new FormData();
        formData.append('file', pdfBlob, `case-progress-${data.caseInfo?.caseNo || Date.now()}.pdf`);
        formData.append('moduleName', data.moduleName || 'client_timeline_history');
        if (data.caseId !== undefined && data.caseId !== null) {
            formData.append('id', data.caseId);
        }

        const response = await httpClient.post(server.CLIENT_TIMELINE_HISTORY + '/upload-pdf-timeline', formData, {
            headers: { 'Content-Type': 'multipart/form-data' },
        });

        // The backend's `url`/`shortUrl` are built from its own (often
        // internal) view of host/port/path — not reliable for external
        // clients. Rebuild both using the known-correct public base URL,
        // keeping only the folder/filename/key (or shortCode) from the
        // backend's response.
        const backendUrl = new URL(response.data.url);
        const key = backendUrl.searchParams.get('key');
        const publicUrl = `${previewFileUrl}raw/${response.data.folder}/${response.data.filename}?key=${encodeURIComponent(key)}`;
        const publicShortUrl = response.data.shortCode
            ? `${previewFileUrl}preview/${response.data.folder}/${response.data.shortCode}`
            : publicUrl; // fall back if an older backend doesn't return shortCode yet

        const result = { ...response.data, url: publicUrl, shortUrl: publicShortUrl };

        // Prefer the short, key-free link when opening/sharing.
        if (open) window.open(result.shortUrl, '_blank');
        return result;
    }

    /**
     * Shared internal step used by both exportToPDF and createToPDF —
     * everything that used to be inside exportToPDF's try block, unchanged,
     * just extracted so it isn't duplicated in two places.
     */
    async _generatePdfBlob(data) {
        this.data = data;
        const currentYear = new Date().getFullYear();

        const element = document.createElement("div");
        element.style.fontFamily = "'Sarabun', sans-serif";
        element.style.padding = "20px 20px";
        element.style.color = "#111827";
        element.style.backgroundColor = "#ffffff";
        element.innerHTML = this.buildHTML();

        // 🌟 แนบโครงร่างเข้า DOM ชั่วคราว เพื่อให้ JavaScript คำนวณความสูงและพิกัดการตัดหน้ากระดาษที่แท้จริงได้
        element.style.position = "absolute";
        element.style.left = "-9999px";
        element.style.width = "175mm"; // หน้ากว้างสุทธิของ A4 (210mm) หักขอบซ้ายขวาออกด้านละ 15mm กับ padding 20px
        document.body.appendChild(element);

        // 🌟 สคริปต์คำนวณและยัด margin-top 5px ให้เฉพาะกล่องที่หลุดไปขึ้นหน้าใหม่
        const rows = element.querySelectorAll(".timeline-row");
        const pageHeightPx = 1060; // ความสูงโดยประมาณของพื้นที่พิมพ์ใน 1 หน้าแผ่นงาน A4 (หน่วยพิกเซล)

        rows.forEach((row) => {
            const elementTop = row.offsetTop;

            // ตรวจสอบว่ากล่องนี้ถูกดันไปอยู่หน้า 2 เป็นต้นไปหรือไม่ (ไม่ใช่หน้าแรก)
            if (elementTop > pageHeightPx) {
                const positionInPage = elementTop % pageHeightPx;

                // ถ้าตำแหน่งหัวกล่องมันชิดขอบบนของหน้าใหม่เกินไป (น้อยกว่า 40px จากขอบบนหน้าใหม่)
                if (positionInPage < 40) {
                    row.style.marginTop = "5px"; // 🌟 สั่งเพิ่มระยะเฉพาะกล่องนั้นๆ ทันที
                }
            }
        });

        // ลบออกจาก DOM หลักหลังจากคำนวณและฉีดสไตล์เสร็จเรียบร้อย
        document.body.removeChild(element);
        element.style.position = "";
        element.style.left = "";
        element.style.width = "";

        const options = {
            margin: [15, 15, 20, 15],
            image: { type: 'jpeg', quality: 0.98 },
            html2canvas: { scale: 2.5, useCORS: true, logging: false },
            jsPDF: { unit: 'mm', format: 'a4', orientation: 'portrait' }
        };

        const worker = html2pdf().set(options).from(element).toPdf().get('pdf');

        await worker.then((pdf) => {
            const totalPages = pdf.internal.getNumberOfPages();

            for (let i = 1; i <= totalPages; i++) {
                pdf.setPage(i);
                pdf.setFont("Sarabun", "normal");
                pdf.setFontSize(9);
                pdf.setTextColor(19, 65, 126);

                const footerText = `Powered by Dtime  |  (c) ${currentYear} Digital Corporate Management Co., Ltd. All rights reserved.`;

                const pageWidth = pdf.internal.pageSize.getWidth() || 210;
                const pageHeight = pdf.internal.pageSize.getHeight() || 297;
                const centerX = pageWidth / 2;

                pdf.text(footerText, centerX, pageHeight - 10, {
                    align: 'center',
                    maxWidth: pageWidth - 30
                });
            }
        });

        return await worker.outputPdf('blob');
    }

    buildHTML() {
        const d = this.data;

        return `
        <style>
            .pdf-container { width: 100%; box-sizing: border-box; font-size: 13px; line-height: 1.5; color: #374151; background: #ffffff; position: relative; }
            
            .pdf-body-content {
                background-color: #FBFBFB;
                padding: 20px;
                border-radius: 12px;
                margin-bottom: 20px;
            }

            /* Header */
            .header-title { font-size: 26px; font-weight: 800; color: #0A1F44; margin: 0 0 6px 0; letter-spacing: -0.5px; }
            .subtitle { color: #13417E; margin: 0; font-size: 13px; }

            /* Card Styles */
            .main-card {
                background: #ffffff;
                border: 1px solid #E5E7EB;
                border-radius: 16px;
                padding: 20px 24px;
                margin-bottom: 16px;
                box-shadow: 0 1px 3px rgba(0,0,0,0.02);
            }

            /* Case Card Grid */
            .case-grid { display: grid; grid-template-columns: 40px 1.1fr 1.2fr 1.2fr; gap: 5px; align-items: start; }
            .card-col { padding: 0 12px; border-right: 1px solid #E5E7EB; height: 100%; display: flex; flex-direction: column; justify-content: start; }
            .card-col:last-child { border-right: none; }
            
            /* Custom CSS Icons */
            .folder-icon-bg { width: 48px; height: 48px; background: #E6EEF9; border-radius: 50%; display: flex; align-items: center; justify-content: center; margin: 0 auto; }
            .folder-icon { width: 20px; height: 14px; background: #13417E; border-radius: 2px; position: relative; }
            .folder-icon::before { content: ''; position: absolute; top: -3px; left: 0; width: 8px; height: 4px; background: #13417E; border-radius: 1px; }

            .clock-icon-bg { width: 36px; height: 36px; background: #E6EEF9; border-radius: 50%; display: flex; align-items: center; justify-content: center; }
            .clock-icon { width: 16px; height: 16px; border: 2px solid #13417E; border-radius: 50%; position: relative; }
            .clock-icon::after { content: ''; position: absolute; top: 3px; left: 7px; width: 2px; height: 5px; background: #13417E; }
            .clock-icon::before { content: ''; position: absolute; top: 7px; left: 7px; width: 4px; height: 2px; background: #13417E; }

            .label { font-size: 11px; color: #7b7a7a; font-weight: 700; margin-bottom: 4px; }
            .value { font-size: 13.5px; color: #1F2937; font-weight: 500; }
            .value-bold { font-weight: 700; color: #111827; }
            
            /* Badge Status */
            .status-badge { background: #E6EEF9; color: #0953E5; font-weight: 600; font-size: 11px; padding: 4px 12px; border-radius: 20px; display: inline-block; width: fit-content; }
            .update-row {
                display: table;
                width: 100%;
                border-collapse: collapse;
            }
            .update-col-left {
                display: table-cell;
                vertical-align: middle;
                text-align: left;
                width: 65%;
                padding-right: 15px;
            }
            .update-col-right {
                display: table-cell;
                vertical-align: middle;
                text-align: right;
                width: 35%;
            }

            /* Timeline */
            .timeline-container { position: relative; margin-top: 15px; padding-left: 10px; }
            
            .timeline-row { 
                display: flex; 
                margin-bottom: 12px; 
                position: relative; 
                z-index: 2; 
                align-items: stretch; 
                page-break-inside: avoid !important;
                break-inside: avoid !important;
            }
            .timeline-row:last-child { margin-bottom: 0; }
            
            .timeline-left-node { width: 32px; display: flex; justify-content: center; align-items: flex-start; padding-top: 14px; margin-right: 16px; position: relative; }
            
            .timeline-left-node::after {
                content: '';
                position: absolute;
                top: 40px;
                bottom: -27px;
                width: 2px;
                background: #d1d5db;
                z-index: 1;
            }
            .timeline-row:last-child .timeline-left-node::after {
                display: none !important;
            }
            
            .node-circle { width: 26px; height: 26px; border-radius: 50%; display: flex; align-items: center; justify-content: center; background: #FFF; box-shadow: 0 0 0 4px white; position: relative; z-index: 5; }
            .checkmark { width: 5px; height: 9px; border: solid white; border-width: 0 2px 2px 0; transform: rotate(45deg); margin-bottom: 1px; }

            .timeline-box { flex: 1; background: #ffffff; border: 1px solid #d1d5db; border-radius: 12px; padding: 14px 20px; position: relative; }
            .timeline-box.highlight { background: #F1F6FA; border: none; }
            
            .timeline-arrow { position: absolute; top: 20px; left: -5px; width: 10px; height: 10px; background: #F1F6FA; transform: rotate(45deg); border-radius: 1px; }

            .time-text { font-size: 12px; color: #7b7a7a; margin-bottom: 3px; }
            .node-title { font-size: 14px; font-weight: 700; color: #111827; margin-bottom: 4px; }
            .node-desc { font-size: 13px; color: #6b7280; line-height: 1.4; }
            .worker-text { color: #13417E; font-size: 12px; font-weight: 500; white-space: nowrap; }

            /* Alertbox */
            .warning-box { display: flex; background: #FFF9EB; border: 1px solid #FAEABA; border-radius: 12px; padding: 16px 20px; margin-top: 20px; align-items: center; }
            .warning-icon-bg { width: 40px; height: 40px; background: #FAEABA; border-radius: 50%; display: flex; align-items: center; justify-content: center; margin-right: 16px; flex-shrink: 0; }
            .warning-icon-char { color: #DAA30B; font-weight: bold; font-size: 22px; }
            .warning-title { font-weight: 700; color: #111827; font-size: 13.5px; margin-bottom: 2px; }
            .warning-desc { color: #374151; font-size: 13px; }
        </style>

        <div class="pdf-container">
            <div style="display: table; width: 100%; border-collapse: collapse; margin-bottom: 30px;">
                <div style="display: table-cell; vertical-align: middle; text-align: right;">
                    <img src="${logo}" style="height: 55px; max-height: 55px; object-fit: contain; display: inline-block;" alt="Logo" />
                </div>
            </div>
            <div style="display: table; width: 100%; border-collapse: collapse; margin-bottom: 30px;">
                <div style="display: table-cell; vertical-align: middle; text-align: left;">
                    <div class="header-title">Case Progress</div>
                    <div class="subtitle">This page allows you to view the latest progress of your case.</div>
                </div>
            </div>

            <div class="pdf-body-content">
                
                <div class="main-card case-grid">
                    <div>
                        <div class="folder-icon-bg"><div class="folder-icon"></div></div>
                    </div>
                    <div class="card-col">
                        <div class="label">Case No.</div>
                        <div class="value value-bold" style="font-size: 16px;">${d.caseInfo.caseNo}</div>
                        <div class="label" style="margin-top: 12px;">Client</div>
                        <div class="value" style="font-size: 13px;">${d.caseInfo.client}</div>
                    </div>
                    <div class="card-col">
                        <div class="label">Case Type</div>
                        <div class="value">${d.caseInfo.caseType}</div>
                    </div>
                    <div class="card-col">
                        <div class="label">Current Status</div>
                        <div style="margin-bottom: 10px;">
                            <span class="status-badge">${d.caseInfo.status}</span>
                        </div>
                        <div class="label">Last Updated</div>
                        <div class="value" style="font-size: 13px; display: flex; align-items: center; gap: 4px; color: #111827;">
                            <span style="font-size: 11px;">📅</span> ${d.caseInfo.lastUpdated}
                        </div>
                    </div>
                </div>

                <div class="main-card">
                    <div class="update-row">
                        <div class="update-col-left" style="font-size: 13px; color: #111827;">
                            <div class="label" style="color: #13417E; font-size: 14px; margin-bottom: 8px;">Latest Update</div>
                            ${d.latestUpdate.message}
                        </div>
                        
                        <div class="update-col-right">
                            <div style="display: inline-flex; align-items: center; gap: 12px; text-align: left;">
                                <div class="clock-icon-bg"><div class="clock-icon"></div></div>
                                <div>
                                    <div class="label" style="margin-bottom: 1px;">Last Updated:</div>
                                    <div class="value-bold" style="font-size: 12px;">${d.latestUpdate.date}</div>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>

                ${d.timelineData && d.timelineData.length > 0 ? `
                    <div class="main-card" style="padding: 20px 24px; margin-bottom: 0;">
                        <div class="label" style="color: #13417E; font-size: 14px; margin-bottom: 15px;">Progress Timeline</div>
                        
                        <div class="timeline-container">
                            ${d.timelineData.map((item, index) => {
            const colors = ['#13417E', '#3385F0', '#80ABEA', '#E0E9F7', '#E6EEF9'];
            const isLast = index === d.timelineData.length - 1;

            let bgColor = colors[Math.min(index, colors.length - 1)];
            let borderStyle = "none";

            if (isLast) {
                bgColor = "transparent";
                borderStyle = "2px solid #E6EEF9";
            }

            const isFirst = index === 0 ? "highlight" : "";

            return `
                                    <div class="timeline-row">
                                        <div class="timeline-left-node">
                                            <div class="node-circle" style="background-color: ${bgColor}; border: ${borderStyle};">
                                                ${(!isLast) ? '<div class="checkmark"></div>' : ''}
                                            </div>
                                        </div>
                                        <div class="timeline-box ${isFirst}">
                                            ${index === 0 ? '<div class="timeline-arrow"></div>' : ''}
                                            <div style="display: flex; justify-content: space-between; align-items: flex-start;">
                                                <div style="max-width: 75%;">
                                                    <div class="time-text">${item.status_time}</div>
                                                    <div class="node-title">${item.status}</div>
                                                    <div class="node-desc">${item.status_note || ''}</div>
                                                </div>
                                                <div class="worker-text">${item.worker || ''}</div>
                                            </div>
                                        </div>
                                    </div>
                                `;
        }).join('')}
                        </div>
                    </div>
                ` : ''}

                <div class="warning-box">
                    <div class="warning-icon-bg">
                        <div class="warning-icon-char">i</div>
                    </div>
                    <div>
                        <div class="warning-title">This page is for viewing progress only</div>
                        <div class="warning-desc">If you have questions, please contact our team directly by email.</div>
                    </div>
                </div>

            </div>
            
        </div>
    `;
    }
}

export default new PDFCaseProgress();